import { DateTime, IANAZone } from 'luxon'

import {
  permanentDstLabel,
  resolveDisplayZone,
  resolveWallClockZone,
} from '../../../ts-utils/permanent-dst'

import type {
  CivilDate,
  CivilTime,
  VenueLocalInstant,
  VenueLocalParts,
} from './types'

export const UTC_ZONE = 'UTC'

/**
 * Every date these views show is anchored to the VENUE's zone, never the
 * browser's. A missing or unparseable zone falls back to UTC — falling back to
 * the browser would make the same game land on different calendar days for two
 * admins looking at the same schedule.
 */
export const resolveTimeZone = (timeZone?: string | null): string => {
  if (typeof timeZone !== 'string' || timeZone.trim().length === 0) {
    return UTC_ZONE
  }
  return IANAZone.isValidZone(timeZone) ? timeZone : UTC_ZONE
}

/**
 * An ISO value that carries `Z` or an explicit offset names an instant; one
 * without names a wall clock, which `fromISO` reads as already being in `zone`.
 * Only the first kind can be re-read in another zone — re-reading the second
 * would move the very wall clock the caller handed us.
 */
const CARRIES_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i

const parseInstant = (iso?: string | null, timeZone?: string | null) => {
  const zone = resolveTimeZone(timeZone)
  if (typeof iso !== 'string' || iso.trim().length === 0) {
    return { dt: null, zone }
  }
  const dt = DateTime.fromISO(iso, { zone })
  if (!dt.isValid) {
    return { dt: null, zone }
  }

  //  A zone whose DST rules this runtime still has wrong is read through one
  //  that is permanently on the right offset. The instant never moves — only
  //  the clock it is read on. See ts-utils/permanent-dst.
  if (!CARRIES_OFFSET.test(iso.trim())) {
    return { dt, zone }
  }
  const display = resolveDisplayZone(zone, dt.toMillis())
  return { dt: display === zone ? dt : dt.setZone(display), zone }
}

/** Splits an absolute instant into the venue's wall-clock date and time. */
export const toVenueLocalParts = (
  iso?: string | null,
  timeZone?: string | null
): VenueLocalParts => {
  const { dt, zone } = parseInstant(iso, timeZone)

  if (dt === null) {
    return {
      date: '',
      time: '',
      offsetName: '',
      timeZone: zone,
      isValid: false,
    }
  }

  return {
    date: dt.toFormat('yyyy-MM-dd'),
    time: dt.toFormat('HH:mm'),
    //  A zone that abolished its clock change gets the label its residents
    //  use; IANA renames the offset after the standard zone that shares it, so
    //  even a current runtime would say MST for a BC game.
    offsetName: permanentDstLabel(zone, dt.toMillis()) ?? dt.toFormat('ZZZZ'),
    timeZone: zone,
    isValid: true,
  }
}

export const venueLocalDate = (
  iso?: string | null,
  timeZone?: string | null
): CivilDate => toVenueLocalParts(iso, timeZone).date

export const formatVenueLocal = (
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  format: string
): string => {
  const { dt } = parseInstant(iso, timeZone)
  return dt === null ? '' : dt.toFormat(format)
}

const CIVIL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const CIVIL_TIME = /^(\d{1,2}):(\d{2})$/

export const isCivilDate = (value: unknown): value is CivilDate =>
  typeof value === 'string' && CIVIL_DATE.test(value)

export const isCivilTime = (value: unknown): value is CivilTime =>
  typeof value === 'string' && CIVIL_TIME.test(value)

interface WallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

const parseWallClock = (date: CivilDate, time: CivilTime): WallClock | null => {
  const dateMatch = CIVIL_DATE.exec(date)
  const timeMatch = CIVIL_TIME.exec(time)
  if (dateMatch === null || timeMatch === null) {
    return null
  }
  return {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
  }
}

const wallClockMatches = (dt: DateTime, wall: WallClock): boolean =>
  dt.year === wall.year &&
  dt.month === wall.month &&
  dt.day === wall.day &&
  dt.hour === wall.hour &&
  dt.minute === wall.minute

/**
 * Converts a venue wall clock to an absolute instant, reporting the two ways a
 * wall clock can fail to name exactly one instant.
 *
 * Works by candidate-and-verify rather than by trusting a single conversion:
 * treat the wall clock as if it were UTC, subtract each plausible offset around
 * that moment, then keep only the candidates that actually read back as the
 * requested wall clock.
 *
 * - **nonexistent** (spring forward): no candidate reads back. `iso` is null and
 *   the caller must refuse the edit — there is no such local time.
 * - **ambiguous** (fall back): two distinct candidates read back. We return the
 *   EARLIER one — the pre-transition occurrence, while the offset is still the
 *   summer one. This matches Luxon's own default and every calendar app users
 *   are coming from, and it is the occurrence a person picking "1:30 AM" on the
 *   fall-back night means when they are scheduling forward through the evening.
 */
export const venueLocalToInstant = (
  date: CivilDate,
  time: CivilTime,
  timeZone?: string | null
): VenueLocalInstant => {
  const zone = resolveTimeZone(timeZone)
  const wall = parseWallClock(date, time)

  if (wall === null) {
    return { iso: null, resolution: 'nonexistent', timeZone: zone }
  }

  const asIfUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute
  )

  //  The write path runs on the viewer's timezone database too, so a typed
  //  wall clock is converted through the same corrected zone the read path
  //  displays in — otherwise an edit on a stale runtime silently stores the
  //  abolished offset. The zone IDENTITY returned to callers stays the real
  //  one; only the arithmetic uses the substitute.
  const computeZone = resolveWallClockZone(zone, asIfUtc)

  const firstGuess = DateTime.fromObject(wall, { zone: computeZone })
  const offsets = new Set<number>([firstGuess.offset])
  //  Luxon resolves an ambiguous wall clock to ONE instant, so its offset alone
  //  can never reveal the second occurrence. Sampling a day either side always
  //  straddles a single transition, which surfaces both offsets in play.
  const DAY_MS = 24 * 60 * 60 * 1000
  offsets.add(
    DateTime.fromMillis(asIfUtc - DAY_MS, { zone: computeZone }).offset
  )
  offsets.add(
    DateTime.fromMillis(asIfUtc + DAY_MS, { zone: computeZone }).offset
  )
  //  The offset in force at the naive candidate may differ from the offset in
  //  force at the real instant; feeding it back covers sub-day transitions.
  offsets.add(
    DateTime.fromMillis(asIfUtc - firstGuess.offset * 60000, {
      zone: computeZone,
    }).offset
  )

  const candidates: number[] = []
  offsets.forEach((offset) => {
    const millis = asIfUtc - offset * 60000
    const back = DateTime.fromMillis(millis, { zone: computeZone })
    if (wallClockMatches(back, wall) && !candidates.includes(millis)) {
      candidates.push(millis)
    }
  })

  if (candidates.length === 0) {
    return { iso: null, resolution: 'nonexistent', timeZone: zone }
  }

  candidates.sort((a, b) => a - b)
  const chosen = candidates[0]

  return {
    iso: DateTime.fromMillis(chosen, { zone: UTC_ZONE }).toISO(),
    resolution: candidates.length > 1 ? 'ambiguous' : 'ok',
    timeZone: zone,
  }
}

/**
 * Moves an instant by whole LOCAL calendar days.
 *
 * "One day later" is a calendar operation, not 1440 minutes: across a DST
 * boundary those differ by an hour, and a game moved by 1440 minutes lands an
 * hour off its slot.
 *
 * Deliberately NOT `dt.plus({ days })`. Luxon normalizes a result that lands in
 * a spring-forward gap by sliding it forward, so 02:30 on 2026-03-07 in
 * Vancouver becomes **03:30** the next day — the wall clock the admin asked to
 * keep is silently changed and nothing reports it. Instead the target wall clock
 * is reconstructed from the date and re-validated, so a gap comes back as
 * `nonexistent` and a fall-back repeat as `ambiguous`, exactly as for a directly
 * entered time.
 */
export const shiftByLocalDays = (
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  days: number
): VenueLocalInstant => {
  const zone = resolveTimeZone(timeZone)
  const parts = toVenueLocalParts(iso, timeZone)

  //  Whole days only. Luxon happily adds 1.5 days, and formatting back to a
  //  civil date then drops the remainder — the game lands on a day nobody asked
  //  for, and a negative fraction lands on a different one again.
  if (!parts.isValid || !Number.isInteger(days)) {
    return { iso: null, resolution: 'nonexistent', timeZone: zone }
  }

  //  Advance the DATE only; the time of day is carried across untouched and
  //  then has to prove it exists on the new date.
  const targetDate = DateTime.fromISO(parts.date, { zone: UTC_ZONE })
    .plus({ days })
    .toFormat('yyyy-MM-dd')

  return venueLocalToInstant(targetDate, parts.time, timeZone)
}

/**
 * Adds whole days to a civil date, with no zone involved at all.
 *
 * For the TBD sentinel, whose day is a UTC calendar date rather than a moment
 * in any venue's zone.
 */
export const shiftCivilDate = (
  date: CivilDate,
  days: number
): CivilDate | null => {
  const parsed = DateTime.fromISO(date, { zone: UTC_ZONE })
  //  WHOLE days, as the name says. Luxon accepts 1.5 and the format back to a
  //  civil date then truncates it, so the sentinel would land on a day nobody
  //  asked for. The planner refuses fractions before it gets here, but this is
  //  exported and must not depend on its only caller for correctness.
  if (!parsed.isValid || !Number.isInteger(days)) {
    return null
  }
  return parsed.plus({ days }).toFormat('yyyy-MM-dd')
}

/** Moves an instant by absolute minutes — wall clock may shift across DST. */
export const shiftByMinutes = (
  iso: string | null | undefined,
  minutes: number
): string | null => {
  const { dt } = parseInstant(iso, UTC_ZONE)
  if (dt === null || !Number.isFinite(minutes)) {
    return null
  }
  return dt.plus({ minutes }).toUTC().toISO()
}

/** Replaces the local date, keeping the local time of day. */
export const setVenueLocalDate = (
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  date: CivilDate
): VenueLocalInstant => {
  const parts = toVenueLocalParts(iso, timeZone)
  const zone = resolveTimeZone(timeZone)
  if (!parts.isValid) {
    return { iso: null, resolution: 'nonexistent', timeZone: zone }
  }
  return venueLocalToInstant(date, parts.time, timeZone)
}

/** Replaces the local time of day, keeping the local date. */
export const setVenueLocalTime = (
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  time: CivilTime
): VenueLocalInstant => {
  const parts = toVenueLocalParts(iso, timeZone)
  const zone = resolveTimeZone(timeZone)
  if (!parts.isValid) {
    return { iso: null, resolution: 'nonexistent', timeZone: zone }
  }
  return venueLocalToInstant(parts.date, time, timeZone)
}

/**
 * Re-anchors `ends_at` to a moved `starts_at`, preserving the ORIGINAL absolute
 * duration.
 *
 * Absolute, not local: a 90-minute game is 90 minutes of ice whether or not the
 * clocks changed underneath it. A null end stays null — the API treats that as
 * "no declared end", and inventing one here would write data the admin never
 * entered.
 */
export const preserveDuration = (
  originalStartIso: string | null | undefined,
  originalEndIso: string | null | undefined,
  nextStartIso: string | null | undefined
): string | null => {
  if (
    typeof originalEndIso !== 'string' ||
    originalEndIso.trim().length === 0
  ) {
    return null
  }

  const start = DateTime.fromISO(originalStartIso ?? '', { zone: UTC_ZONE })
  const end = DateTime.fromISO(originalEndIso, { zone: UTC_ZONE })
  const next = DateTime.fromISO(nextStartIso ?? '', { zone: UTC_ZONE })

  if (!start.isValid || !end.isValid || !next.isValid) {
    return null
  }

  return next.plus({ milliseconds: end.toMillis() - start.toMillis() }).toISO()
}

/**
 * `true` when the loaded games span more than one venue zone, so the grid must
 * label its time column and offer browser-local tooltips.
 */
export const spansMultipleZones = (
  timeZones: Array<string | null | undefined>
): boolean => {
  const resolved = new Set(timeZones.map(resolveTimeZone))
  return resolved.size > 1
}

/**
 * Today, as the VIEWER's civil date.
 *
 * One function, because the calendar's rendered viewport and the query window it
 * fetches must agree on what day it is. They did not: the viewport defaulted to
 * the viewer's local date and the fetch window to UTC's, so at 18:00 on August
 * 31 in Vancouver the grid drew August while the request asked for September,
 * and the month came up nearly empty before a single cell rendered.
 *
 * Local rather than UTC because a calendar opening on "tomorrow" for everyone
 * west of Greenwich in the evening is simply wrong.
 */
export const todayCivilDate = (): string =>
  DateTime.local().toFormat('yyyy-MM-dd')
